import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Clock, CreditCard, Shield, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  icon: React.ReactNode;
  category: string;
}

const faqItems: FAQItem[] = [
  {
    id: 1,
    question: "Wie viel kostet eine App-Entwicklung bei Ihnen?",
    answer: "Unsere kosteneffizienten Lösungen starten bei CHF 2'500 für eine Web-App. Mit KI-Integration ab CHF 4'500. Der finale Preis hängt von Ihren spezifischen Anforderungen ab. Nutzen Sie unseren Preisrechner für eine sofortige Schätzung!",
    icon: <CreditCard className="w-5 h-5" />,
    category: "Preise"
  },
  {
    id: 2,
    question: "Wie lange dauert die Entwicklung einer App?",
    answer: "Standard-Projekte realisieren wir in 4-6 Wochen. Express-Service in 2-3 Wochen möglich (Aufpreis 50%). Wir starten sofort nach Auftragserteilung und halten Sie täglich auf dem Laufenden.",
    icon: <Clock className="w-5 h-5" />,
    category: "Zeit"
  },
  {
    id: 3,
    question: "Was bedeutet KI-Integration konkret?",
    answer: "KI-Integration bedeutet: Automatische Datenanalyse, intelligente Chatbots, Bilderkennung, Sprachverarbeitung und predictive Analytics. Diese Features sparen Ihnen täglich 2-3 Stunden Arbeitszeit durch Automatisierung von Routineaufgaben.",
    icon: <Globe className="w-5 h-5" />,
    category: "KI"
  },
  {
    id: 4,
    question: "Wo werden meine Daten gespeichert?",
    answer: "100% in der Schweiz! Wir nutzen ausschliesslich Schweizer Rechenzentren. Ihre Daten unterliegen dem strengen Schweizer Datenschutzgesetz. Keine Weitergabe an Dritte, volle DSGVO-Konformität.",
    icon: <Shield className="w-5 h-5" />,
    category: "Sicherheit"
  },
  {
    id: 5,
    question: "Bieten Sie Support in meiner Sprache?",
    answer: "Ja! Wir bieten Support in Deutsch, Französisch, Italienisch und Englisch. Persönliche Betreuung in Ihrer Muttersprache - typisch schweizerisch mehrsprachig.",
    icon: <HelpCircle className="w-5 h-5" />,
    category: "Support"
  },
  {
    id: 6,
    question: "Kann ich meine bestehenden Systeme integrieren?",
    answer: "Absolut! Wir integrieren Ihre App nahtlos in bestehende Systeme wie SAP, Salesforce, Microsoft 365 oder Ihre eigene Software. API-Anbindungen sind unsere Spezialität.",
    icon: <Globe className="w-5 h-5" />,
    category: "Integration"
  },
  {
    id: 7,
    question: "Wie sieht es mit Updates und Wartung aus?",
    answer: "6 Monate kostenlose Updates und Support inklusive! Danach optional: Wartungsvertrag ab CHF 99/Monat. Sie können die App aber auch selbst warten - wir übergeben den kompletten Code.",
    icon: <Shield className="w-5 h-5" />,
    category: "Wartung"
  },
  {
    id: 8,
    question: "Arbeiten Sie auch für kleine Unternehmen?",
    answer: "Besonders gerne! 80% unserer Kunden sind KMUs mit 1-50 Mitarbeitern. Unsere kosteneffizienten Lösungen sind speziell für Schweizer KMUs konzipiert. Keine versteckten Kosten, transparente Preise.",
    icon: <CreditCard className="w-5 h-5" />,
    category: "Zielgruppe"
  }
];

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Alle');

  const categories = ['Alle', ...Array.from(new Set(faqItems.map(item => item.category)))];

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const filteredItems = selectedCategory === 'Alle' 
    ? faqItems 
    : faqItems.filter(item => item.category === selectedCategory);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Häufig gestellte Fragen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Alles was Schweizer Unternehmen über unsere App-Entwicklung und KI-Integration wissen möchten
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(category => (
            <motion.button
              key={category}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full transition-all ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
              }`}
            >
              {category}
            </motion.button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto space-y-4">
          <AnimatePresence>
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item.question}
                    </h3>
                  </div>
                  <motion.div
                    animate={{ rotate: openItems.includes(item.id) ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {openItems.includes(item.id) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 leading-relaxed pl-14">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-4">
            Haben Sie weitere Fragen? Wir sind für Sie da!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Kostenloses Beratungsgespräch
            </motion.button>
            <motion.a
              href="tel:+41XXXXXXXXX"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 bg-white text-blue-600 rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center"
            >
              <Clock className="w-5 h-5 mr-2" />
              Direkt anrufen
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
